import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext(null);

// Auto mode: Dark between 7 PM (19:00) and 6 AM (06:00)
const DARK_START_HOUR = 19; // 7 PM
const DARK_END_HOUR = 6;    // 6 AM

function getTimeBasedTheme() {
  const hour = new Date().getHours();
  // Dark mode if hour >= 19 (7 PM) OR hour < 6 (6 AM)
  return (hour >= DARK_START_HOUR || hour < DARK_END_HOUR) ? 'dark' : 'light';
}

function getInitialThemeMode() {
  if (typeof window === 'undefined') return 'light';
  const saved = localStorage.getItem('themeMode');
  if (saved && ['light', 'dark', 'auto'].includes(saved)) return saved;
  return 'light';
}

function getInitialAppliedTheme(themeMode) {
  if (themeMode === 'auto') return getTimeBasedTheme();
  return themeMode;
}

export function ThemeProvider({ children }) {
  // Theme can be 'light', 'dark', or 'auto'
  const [themeMode, setThemeMode] = useState(getInitialThemeMode);
  
  // The actual applied theme (always 'light' or 'dark')
  const [appliedTheme, setAppliedTheme] = useState(() => getInitialAppliedTheme(getInitialThemeMode()));

  // Update applied theme when themeMode changes
  const updateAppliedTheme = useCallback(() => {
    if (themeMode === 'auto') {
      setAppliedTheme(getTimeBasedTheme());
    } else {
      setAppliedTheme(themeMode);
    }
  }, [themeMode]);

  // Apply theme to DOM
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(appliedTheme);
  }, [appliedTheme]);

  // Save theme mode and update applied theme
  useEffect(() => {
    localStorage.setItem('themeMode', themeMode);
    // Use setTimeout to avoid the setState-in-effect lint warning
    const timer = setTimeout(updateAppliedTheme, 0);
    return () => clearTimeout(timer);
  }, [themeMode, updateAppliedTheme]);

  // Auto mode: Check time every minute to update theme
  useEffect(() => {
    if (themeMode !== 'auto') return;

    const interval = setInterval(() => {
      const newTheme = getTimeBasedTheme();
      setAppliedTheme(prev => prev !== newTheme ? newTheme : prev);
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [themeMode]);

  // Cycle through themes: light -> dark -> auto -> light
  const cycleTheme = useCallback(() => {
    setThemeMode(prev => {
      if (prev === 'light') return 'dark';
      if (prev === 'dark') return 'auto';
      return 'light';
    });
  }, []);

  // Simple toggle between light and dark (skips auto)
  const toggleTheme = useCallback(() => {
    setThemeMode(prev => prev === 'light' ? 'dark' : 'light');
  }, []);

  // Set theme mode directly
  const setTheme = useCallback((mode) => {
    if (['light', 'dark', 'auto'].includes(mode)) {
      setThemeMode(mode);
    }
  }, []);

  const value = {
    theme: appliedTheme,         // The actual applied theme ('light' or 'dark')
    themeMode,                   // The user's preference ('light', 'dark', or 'auto')
    setTheme,                    // Set theme mode directly
    toggleTheme,                 // Toggle between light/dark
    cycleTheme,                  // Cycle through light/dark/auto
    isDark: appliedTheme === 'dark',
    isAuto: themeMode === 'auto',
    autoSchedule: {
      darkStartHour: DARK_START_HOUR,
      darkEndHour: DARK_END_HOUR,
    },
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
